import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@nexus/ui";

import { industryBrandOptionsQueryOptions } from "@/lib/queries";
import { formatBrandName } from "@/features/dashboard/lib/format";

interface IndustryDefaultBrandSelectProps {
  industry: string;
  industryTitle: string;
  value: string;
  onChange: (brand: string) => void;
  disabled: boolean;
  enabled: boolean;
  /** Table row: full-width select only (no inline industry label). */
  variant?: "stacked" | "table";
}

/**
 * One assigned industry's default My Brand, picked from that industry's roster only.
 * The roster loads on first focus, not on render: a table of hundreds of industries
 * must not fire one options job per row.
 */
export function IndustryDefaultBrandSelect({
  industry,
  industryTitle,
  value,
  onChange,
  disabled,
  enabled,
  variant = "stacked",
}: IndustryDefaultBrandSelectProps) {
  const { t } = useTranslation();
  const [isActivated, setIsActivated] = useState(false);
  const { data: brands = [], isLoading, isError, isSuccess } = useQuery(
    industryBrandOptionsQueryOptions(industry, { enabled: enabled && isActivated }),
  );
  const isValueOutsideRoster = !!value && isSuccess && !brands.some((brand) => brand.name === value);
  const rosterState = isSuccess ? "loaded" : isError ? "error" : isLoading ? "loading" : "idle";
  const activate = () => setIsActivated(true);
  const noneLabel = t("iam.shared.none", "None");
  const rosterStatusLabel = isLoading
    ? t("iam.orgDrawer.settings.industryDefaultBrandLoading", "Loading brands…")
    : isError
      ? t("iam.orgDrawer.settings.industryDefaultBrandError", "Couldn't load brands")
      : value
        ? formatBrandName(value)
        : noneLabel;

  const select = (
      <select
        data-testid={`org-settings-industry-default-brand-${industry}`}
        data-roster-state={rosterState}
        aria-label={t("iam.orgDrawer.settings.industryDefaultBrandAria", {
          industry: industryTitle,
          defaultValue: "Default My Brand for {{industry}}",
        })}
        value={value}
        onChange={(event) => {
          if (!isSuccess) return;
          onChange(event.target.value);
        }}
        onFocus={activate}
        onPointerDown={activate}
        disabled={disabled || (isActivated && !isSuccess)}
        className={cn(
          "h-8 min-w-0 w-full rounded-md border border-input-border bg-background px-2.5 text-xs outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50",
          value ? "text-foreground" : "text-muted-foreground",
        )}
      >
        <option value="">{value ? noneLabel : rosterStatusLabel}</option>
        {!!value && !isSuccess && <option value={value}>{rosterStatusLabel}</option>}
        {isValueOutsideRoster && (
          <option value={value}>
            {t("iam.orgDrawer.settings.industryDefaultBrandNotInRoster", {
              brand: value,
              defaultValue: "{{brand}} (not in this industry)",
            })}
          </option>
        )}
        {brands.map((brand) => (
          <option key={brand.name} value={brand.name}>
            {formatBrandName(brand.name)}
          </option>
        ))}
      </select>
  );

  if (variant === "table") {
    return select;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="w-32 shrink-0 truncate text-xs text-foreground" title={industryTitle}>
        {industryTitle}
      </span>
      {select}
    </div>
  );
}
